import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, RefreshCw, ScanLine } from "lucide-react";

/**
 * Barcode + QR camera scanner. Handoff-first: as soon as a code is decoded
 * we call onScan(code) and close so the cashier's flow feels instant.
 *
 * Data-testid: barcode-scanner-modal, barcode-scanner-close, barcode-scanner-switch-camera
 */
export default function BarcodeScannerModal({ open, onClose, onScan }) {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const [error, setError] = useState("");
  const [cameras, setCameras] = useState([]);
  const [activeCam, setActiveCam] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    setStarting(true);
    Html5Qrcode.getCameras()
      .then((devs) => {
        if (cancelled) return;
        if (!devs || devs.length === 0) {
          setError("Kamera bulunamadı. Tarayıcı izni verildi mi?");
          setStarting(false);
          return;
        }
        setCameras(devs);
        // Prefer rear camera on mobile
        const back = devs.find((d) => /back|rear|environment/i.test(d.label || ""));
        const picked = back?.id || devs[devs.length - 1].id;
        setActiveCam(picked);
      })
      .catch((e) => {
        if (cancelled) return;
        setError("Kameraya erişilemedi: " + (e?.message || "İzin reddedildi"));
        setStarting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !activeCam) return;
    let live = true;
    setStarting(true);
    setError("");
    const el = "milyemsoft-barcode-reader";
    // Ensure DOM is ready
    const timer = setTimeout(async () => {
      if (!live) return;
      try {
        const scanner = new Html5Qrcode(el, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          activeCam,
          {
            fps: 12,
            qrbox: (w, h) => {
              const size = Math.min(w, h);
              return { width: Math.floor(size * 0.72), height: Math.floor(size * 0.44) };
            },
            aspectRatio: 1.6,
            disableFlip: false,
          },
          (decoded) => {
            if (!live) return;
            live = false;
            // Vibrate/haptic if supported
            try { navigator.vibrate && navigator.vibrate(60); } catch { /* noop */ }
            onScan?.(String(decoded).trim());
            // Stop asynchronously; user's onScan will close the modal
          },
          () => { /* scan errors per frame — ignore */ }
        );
        setStarting(false);
      } catch (e) {
        setError("Kamera başlatılamadı: " + (e?.message || "Bilinmeyen hata"));
        setStarting(false);
      }
    }, 60);

    return () => {
      live = false;
      clearTimeout(timer);
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        s.stop().catch(() => {}).finally(() => {
          try { s.clear(); } catch { /* noop */ }
        });
      }
    };
  }, [open, activeCam, onScan]);

  if (!open) return null;

  const switchCamera = () => {
    if (cameras.length < 2) return;
    const idx = cameras.findIndex((c) => c.id === activeCam);
    const next = cameras[(idx + 1) % cameras.length];
    setActiveCam(next.id);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      data-testid="barcode-scanner-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md card-flat border border-[--gold]/40 relative"
        style={{ borderColor: "rgba(212,175,55,0.45)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ScanLine size={16} style={{ color: "var(--gold)" }} />
            <div className="label-caps tracking-widest text-xs" style={{ color: "var(--gold)" }}>
              Barkod Tarayıcı
            </div>
          </div>
          <div className="flex items-center gap-1">
            {cameras.length > 1 && (
              <button
                onClick={switchCamera}
                title="Kamerayı değiştir"
                className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-[--gold] hover:text-[--gold] transition"
                data-testid="barcode-scanner-switch-camera"
              >
                <RefreshCw size={14} />
              </button>
            )}
            <button
              onClick={onClose}
              className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-red-400 hover:text-red-300 transition"
              data-testid="barcode-scanner-close"
              aria-label="Kapat"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="relative bg-black" style={{ aspectRatio: "16 / 10" }}>
          <div id="milyemsoft-barcode-reader" className="absolute inset-0" />
          {(starting || error) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-6 pointer-events-none">
              <Camera size={26} className="text-neutral-500" />
              {error ? (
                <div className="text-sm text-red-300">{error}</div>
              ) : (
                <div className="text-xs text-neutral-400">Kamera hazırlanıyor…</div>
              )}
            </div>
          )}
        </div>

        <div className="px-4 py-3 text-[11px] text-neutral-400 border-t border-white/5">
          Etiketi kutunun içine yerleştirin — otomatik okunacak ve sepete düşecek.
        </div>
      </div>
    </div>
  );
}
